/**
 * Module navigation resolution -- Xyden Leadership Assessment Platform
 *
 * Pure (React-free) derivation of the admin sidebar's module label and nav
 * items for a given route + user. Extracted from AdminLayout so the logic --
 * including the multi-role "Switch module" switcher (N2) -- can be unit-tested
 * without rendering the full shell.
 */

import type { NavItem, User } from '../types';

/** Navigation items per module -- derived from frontend_screen_design.md Section 5d */
export const CONTENT_STUDIO_NAV: NavItem[] = [
  { label: "Home", href: "/content-studio", icon: "LayoutDashboard" },
  { label: "Versions", href: "/content-studio/versions", icon: "GitBranch" },
  { label: "Buckets", href: "/content-studio/buckets", icon: "Layers" },
  { label: "Activities", href: "/content-studio/activities", icon: "Activity" },
  { label: "Competency Library", href: "/content-studio/competencies", icon: "BookOpen" },
  { label: "Scoring Rule Sets", href: "/content-studio/scoring-rule-sets", icon: "Gauge" },
  { label: "Question Bank", href: "/content-studio/questions", icon: "HelpCircle" },
  { label: "Triad Matrix", href: "/content-studio/triad-matrix", icon: "Grid3X3" },
  { label: "Maturity Mapping", href: "/content-studio/maturity-mapping", icon: "Gauge" },
  { label: "Triad Selection", href: "/content-studio/scoring/triad-selection", icon: "Grid3X3" },
  { label: "Validation", href: "/content-studio/validation", icon: "ShieldCheck" },
];

export const TEST_ADMIN_NAV: NavItem[] = [
  { label: "Clients & Managers", href: "/test-admin/clients", icon: "Building2" },
  { label: "Tests", href: "/test-admin/tests", icon: "ClipboardList" },
  { label: "Create Test", href: "/test-admin/tests/create", icon: "FilePlus" },
  { label: "Mirror Prompts", href: "/test-admin/mirror-prompts", icon: "Settings" },
];

export const CLIENT_MANAGER_NAV: NavItem[] = [
  { label: "Home", href: "/client", icon: "LayoutDashboard" },
  { label: "Tests", href: "/client/tests", icon: "ClipboardList" },
];

export const SUPER_ADMIN_NAV: NavItem[] = [
  { label: "User Management", href: "/super-admin/users", icon: "Users" },
  { label: "System Config", href: "/super-admin/settings", icon: "Settings" },
  { label: "Audit Log", href: "/super-admin/audit", icon: "ClipboardList" },
  { label: "Content Studio", href: "/content-studio", icon: "LayoutDashboard" },
  { label: "Test Administration", href: "/test-admin/tests", icon: "ClipboardList" },
  { label: "Client Manager", href: "/client", icon: "Building2" },
];

export const BACK_TO_ADMIN: NavItem = {
  label: "Back to Administration",
  href: "/super-admin/users",
  icon: "ShieldCheck",
};

/**
 * Module registry (N2): the role that grants each module, its sidebar label,
 * and its home route. Drives the multi-role "Switch module" switcher. The RBAC
 * model lets a user hold several non-Candidate roles, so a user can be
 * authorized for more than one of these modules at once.
 */
interface ModuleDef {
  label: string;
  role: string;
  home: string;
}

const MODULES: ModuleDef[] = [
  { label: "Content Studio", role: "Content Admin", home: "/content-studio" },
  { label: "Test Administration", role: "Test Administrator", home: "/test-admin/tests" },
  { label: "Client Manager", role: "Client Manager", home: "/client" },
  { label: "Administration", role: "Super Admin", home: "/super-admin/users" },
];

/**
 * Build the "Switch module" section (N2). When a user's roles grant more than
 * one module, this returns a single collapsible group -- expanded by default --
 * linking to the home route of every module they can reach EXCEPT the one they
 * are currently in. Returns [] when the user can reach 0 or 1 modules (nothing
 * to switch to), so single-module users see no switcher. This generalizes the
 * Super-Admin-only SUPER_ADMIN_NAV / BACK_TO_ADMIN cross-module navigation to
 * any multi-role user.
 */
function switchModuleSection(roles: string[], currentLabel: string): NavItem[] {
  const reachable = MODULES.filter((m) => roles.includes(m.role));
  if (reachable.length <= 1) return [];
  const others = reachable.filter((m) => m.label !== currentLabel);
  if (others.length === 0) return [];
  return [
    {
      label: "Switch module",
      icon: "Grid3X3",
      group: true,
      defaultExpanded: true,
      children: others.map((m) => ({ label: m.label, href: m.home })),
    },
  ];
}

/**
 * Resolve the sidebar module label + nav items for a route and user. Path-first,
 * then role-based defaults -- matching the route guards in App.tsx, which allow
 * any user into every module they hold a role for.
 */
export function resolveModuleNavigation(
  pathname: string,
  user: User | null,
): { moduleLabel: string; navItems: NavItem[] } {
  // If user is present, determine nav by role first, then pathname
  if (user) {
    const roles = user.roles ?? [];
    const isSuperAdmin = roles.includes("Super Admin");
    // Super Admins keep their existing "Back to Administration" prepend
    // (SUPER_ADMIN_NAV already links to every module). Any OTHER multi-role
    // user gets a "Switch module" section to the modules they are not in (N2).
    const withSwitcher = (items: NavItem[], currentLabel: string): NavItem[] =>
      isSuperAdmin
        ? [BACK_TO_ADMIN, ...items]
        : [...switchModuleSection(roles, currentLabel), ...items];

    if (pathname.startsWith("/super-admin")) return { moduleLabel: "Administration", navItems: SUPER_ADMIN_NAV };
    if (pathname.startsWith("/test-admin")) return { moduleLabel: "Test Administration", navItems: withSwitcher(TEST_ADMIN_NAV, "Test Administration") };
    if (pathname.startsWith("/client")) return { moduleLabel: "Client Manager", navItems: withSwitcher(CLIENT_MANAGER_NAV, "Client Manager") };
    // Content studio routes -- show content nav
    if (pathname.startsWith("/content-studio")) return { moduleLabel: "Content Studio", navItems: withSwitcher(CONTENT_STUDIO_NAV, "Content Studio") };

    // Default: determine by primary role
    if (roles.includes("Test Administrator")) return { moduleLabel: "Test Administration", navItems: TEST_ADMIN_NAV };
    if (roles.includes("Client Manager")) return { moduleLabel: "Client Manager", navItems: CLIENT_MANAGER_NAV };
    if (isSuperAdmin) return { moduleLabel: "Administration", navItems: SUPER_ADMIN_NAV };
  }

  // Fallback by pathname
  if (pathname.startsWith("/super-admin")) return { moduleLabel: "Administration", navItems: SUPER_ADMIN_NAV };
  if (pathname.startsWith("/test-admin")) return { moduleLabel: "Test Administration", navItems: TEST_ADMIN_NAV };
  if (pathname.startsWith("/client")) return { moduleLabel: "Client Manager", navItems: CLIENT_MANAGER_NAV };
  if (pathname.startsWith("/results")) return { moduleLabel: "Results", navItems: [] };
  return { moduleLabel: "Content Studio", navItems: CONTENT_STUDIO_NAV };
}
